import { useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  Tabs,
  Tab,
  Card,
  CardBody,
} from "@nextui-org/react";
import { LockIcon } from "../../assets/icons/LockIcon";
import { PhoneIcon } from "../../assets/icons/PhoneIcon";
import { useTranslation } from "react-i18next";
import { Alert, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useClubLoginModalState, useClubAuthState } from "src/hooks/useClubsHooks";
import Captcha from "src/utils/Captcha";

export default function ClubLoginModal() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { t } = useTranslation();
  const [, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  
  const { tempToken, imageGoogleAuthUrl, isClub } = useClubAuthState();
  
  const {
    loginMethod,
    loginClubId,
    loginPassword,
    saffCode,
    myClubCode,
    googleAuthCode,
    loginStep,
    captchaValue,
    generatedCaptcha,
    isCaptchaValid,
    hasAlert,
    error,
    successMessage,
    isLoadingLogin,
    isLoadingLoginSaff,
    isLoadingLoginMyClub,
    isLoadingVerify,
    setLoginMethod,
    setLoginClubId,
    setLoginPassword,
    setSaffCode,
    setMyClubCode,
    setGoogleAuthCode,
    setLoginStep,
    setCaptchaValue,
    setGeneratedCaptcha,
    setIsCaptchaValid,
    setHasAlert,
    setError,
    setSuccessMessage,
    resetLoginForm,
    loginClub,
    loginClubSaff,
    loginClubMyClub,
    verifyClub,
  } = useClubLoginModalState();

  useEffect(() => {
    setIsCaptchaValid(captchaValue === generatedCaptcha);
  }, [captchaValue, generatedCaptcha, setIsCaptchaValid]);

  useEffect(() => {
    setHasAlert(!!error || !!successMessage);
  }, [error, successMessage, setHasAlert]);

  useEffect(() => {
    if (isClub) {
      navigate('/homepage');
    }
  }, [isClub, navigate]);

  const validateAndLogin = async () => {
    if (!isCaptchaValid) {
      setError(document.documentElement.dir === "rtl" ? "الرجاء إدخال الكابتشا الصحيح" : "Please enter the correct captcha");
      return;
    }

    setError('');
    setSuccessMessage('');

    if (loginMethod === 'regular') {
      if (!loginClubId || !loginPassword) {
        setError(document.documentElement.dir === "rtl" ? "الرجاء إدخال جميع الحقول المطلوبة" : "Please fill in all required fields");
        return;
      }

      try {
        await loginClub({ id: loginClubId, password: loginPassword });
        setLoginStep(2);
        setSuccessMessage(document.documentElement.dir === "rtl" ? "تم إرسال رمز التحقق إلى تطبيق Google Authenticator" : "Verification code sent to Google Authenticator");
      } catch (error: any) {
        setError(document.documentElement.dir === "rtl" ? "معرف النادي أو كلمة المرور غير صحيحة" : "Invalid club ID or password");
      }
    } else if (loginMethod === 'saff') {
      if (!saffCode) {
        setError(document.documentElement.dir === "rtl" ? "الرجاء إدخال رمز SAFF" : "Please enter SAFF code");
        return;
      }

      try {
        await loginClubSaff({ code: saffCode });
        setSuccessMessage(document.documentElement.dir === "rtl" ? "تم تسجيل الدخول بنجاح" : "Login successful");
        setTimeout(() => {
          onOpenChange();
          navigate('/homepage');
        }, 2000);
      } catch (error: any) {
        setError(document.documentElement.dir === "rtl" ? "رمز SAFF غير صحيح" : "Invalid SAFF code");
      }
    } else if (loginMethod === 'myclub') {
      if (!myClubCode) {
        setError(document.documentElement.dir === "rtl" ? "الرجاء إدخال رمز MyClub" : "Please enter MyClub code");
        return;
      }

      try {
        await loginClubMyClub({ code: myClubCode });
        setSuccessMessage(document.documentElement.dir === "rtl" ? "تم تسجيل الدخول بنجاح" : "MyClub login successful");
        setTimeout(() => {
          onOpenChange();
          navigate('/homepage');
        }, 2000);
      } catch (error: any) {
        setError(document.documentElement.dir === "rtl" ? "رمز MyClub غير صحيح" : "Invalid MyClub code");
      }
    }
  };

  const handleGoogleAuthSubmit = async () => {
    if (!googleAuthCode) {
      setError(document.documentElement.dir === "rtl" ? "الرجاء إدخال رمز التحقق" : "Please enter verification code");
      return;
    }

    try {
      await verifyClub({ otp: googleAuthCode });
      setSuccessMessage(document.documentElement.dir === "rtl" ? "تم التحقق بنجاح" : "Verification successful");
      setTimeout(() => {
        navigate('/homepage');
      }, 1000);
    } catch (error: any) {
      setError(document.documentElement.dir === "rtl" ? "رمز التحقق غير صحيح" : "Invalid verification code");
    }
  };

  const resetForm = () => {
    resetLoginForm();
  };

  return (
    <>
      {contextHolder}
      <Button onPress={onOpen} color="primary">
        {t('club.login')}
      </Button>
      <Modal 
        isOpen={isOpen} 
        onOpenChange={onOpenChange}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {t('club.login.title')}
              </ModalHeader>
              <ModalBody>
                {hasAlert && (
                  <Alert
                    message={error || successMessage}
                    type={error ? "error" : "success"}
                    showIcon
                    closable
                    onClose={() => {
                      setError('');
                      setSuccessMessage('');
                    }}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {loginStep === 1 && (
                  <Tabs 
                    selectedKey={loginMethod} 
                    onSelectionChange={(key) => setLoginMethod(key as 'regular' | 'saff' | 'myclub')}
                    aria-label="Login methods"
                  >
                    <Tab key="regular" title={t('club.login.regular')}>
                      <Card>
                        <CardBody className="gap-4">
                          <Input
                            autoFocus
                            endContent={
                              <PhoneIcon className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                            }
                            label={t('club.id')}
                            placeholder={t('club.id.placeholder')}
                            variant="bordered"
                            value={loginClubId}
                            onChange={(e) => setLoginClubId(e.target.value)}
                          />
                          <Input
                            endContent={
                              <LockIcon className="text-2xl text-default-400 pointer-events-none flex-shrink-0" />
                            }
                            label={t('password')}
                            placeholder={t('password.placeholder')}
                            type="password"
                            variant="bordered"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                          />
                          <Captcha
                            onChange={(value) => {
                              setGeneratedCaptcha(value);
                              setIsCaptchaValid(captchaValue === value);
                            }}
                          />
                        </CardBody>
                      </Card>
                    </Tab>
                    <Tab key="saff" title={t('club.login.saff')}>
                      <Card>
                        <CardBody className="gap-4">
                          <Input
                            autoFocus
                            label={t('club.saff.code')}
                            placeholder={t('club.saff.code.placeholder')}
                            variant="bordered"
                            value={saffCode}
                            onChange={(e) => setSaffCode(e.target.value)}
                          />
                          <p className="text-sm text-gray-600">
                            {t('club.saff.description')}
                          </p>
                        </CardBody>
                      </Card>
                    </Tab>
                    <Tab key="myclub" title={t('club.login.myclub')}>
                      <Card>
                        <CardBody className="gap-4">
                          <Input
                            autoFocus
                            label={t('club.login.myclub.code')}
                            placeholder={t('club.login.myclub.code.placeholder')}
                            variant="bordered"
                            value={myClubCode}
                            onChange={(e) => setMyClubCode(e.target.value)}
                          />
                          <p className="text-sm text-gray-600">
                            {t('club.login.myclub.description')}
                          </p>
                        </CardBody>
                      </Card>
                    </Tab>
                  </Tabs>
                )}

                {loginStep === 2 && tempToken && imageGoogleAuthUrl && (
                  <Card>
                    <CardBody className="gap-4">
                      <div className="text-center">
                        <h3 className="text-lg font-semibold mb-4">
                          {t('club.google.auth.title')}
                        </h3>
                        <img 
                          src={imageGoogleAuthUrl} 
                          alt="Google Authenticator QR Code"
                          className="mx-auto mb-4 max-w-xs"
                        />
                        <p className="text-sm text-gray-600 mb-4">
                          {t('club.google.auth.description')}
                        </p>
                      </div>
                      <Input
                        autoFocus
                        label={t('club.google.auth.code')}
                        placeholder={t('club.google.auth.code.placeholder')}
                        variant="bordered"
                        value={googleAuthCode}
                        onChange={(e) => setGoogleAuthCode(e.target.value)}
                      />
                    </CardBody>
                  </Card>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={() => {
                  resetForm();
                  onClose();
                }}>
                  {t('cancel')}
                </Button>
                {loginStep === 1 && (
                  <Button 
                    color="primary" 
                    onPress={validateAndLogin}
                    isLoading={isLoadingLogin || isLoadingLoginSaff || isLoadingLoginMyClub}
                  >
                    {t('login')}
                  </Button>
                )}
                {loginStep === 2 && (
                  <Button 
                    color="primary" 
                    onPress={handleGoogleAuthSubmit}
                    isLoading={isLoadingVerify}
                  >
                    {t('verify')}
                  </Button>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
